<template>
    <div
        class="question-row question-row-checkbox"
        :data-column-count="props.content.options.column_count.value"
        :data-lead-service-name="name"
    >
        <div
            v-for="(answersGroup, groupKey) in chunkAnswers(props.content.answers, props.content.options.column_count.value)"
            :key="groupKey"
            class="checkbox-row"
        >
            <div
                v-for="(answer, key) in answersGroup"
                :key="key"
                class="checkbox-column"
            >
                <input
                    :id="`question-${instanceId}-${groupKey}-radiobutton-${key}`"
                    :name="`question-${instanceId}-radiobutton`"
                    :data-question-to="answer.next_question?.value"
                    :data-showstopper="answer.showstopper.value"
                    :data-hide-showstopper-close-button="answer.showstopper.hide_showstopper_close_button || false"
                    :data-worktype-id="answer.worktype_id_override?.value"
                    :data-enquiry-dashboard-redirect-version="answer.enquiry_dashboard_redirect_version?.value"
                    :data-label="answer.label.value"
                    :value="answer.ELMO_value.value"
                    :checked="selectedValue === answer.ELMO_value.value"
                    type="radio"
                    @change="updateValue"
                >
                <label :for="`question-${instanceId}-${groupKey}-radiobutton-${key}`">
                    <div class="checkbox checkbox--radio">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 100 100"
                        >
                            <path d="M10,50a40,40 0 1,0 80,0a40,40 0 1,0 -80,0" />
                        </svg>
                    </div>

                    <div class="checkbox-label">
                        {{ answer.label.value }}
                    </div>
                </label>
            </div>
        </div>
    </div>

    <div class="question-row question-row-button">
        <button
            class="question-button-next button"
            type="button"
            value="next"
            data-showstopper="none"
            :data-question-to="props.content.next_question?.value"
            @click="check"
        >
            <zoe-atom-text
                :item="props.content.button.label.value"
            />
        </button>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from 'vuex';
import { addExtraData, getQuestionName, questionProps, storeExtraData } from '@mixins/quiz/v2.0.0/question';
import { sbPopup } from '../../../../../js/vue-flows/default-components/sb-popup/v1/sb-popup-1.1.0';
import { checkCompleted } from '@mixins/quiz/v2.0.0/checks';
import { getNextQuestion } from '@mixins/quiz/v2.0.0/get-next-question';
import { chunkAnswers } from '@mixins/quiz/v2.0.0/helpers';

import ZoeAtomText from '../../../../atoms/text/v1.0.0/zoe-atom-text.vue';
import stripHtml from '../../../../../mixins/utils/strip-html';

const store = useStore();
const props = defineProps(questionProps);

const name = getQuestionName(props);
const uuid = ref(props.content?._info?.uuid);
const instanceId = uuidv4();

const selectedValue = computed(() => store.state.quiz.extradata[name]?.value);

onMounted(() => {
    addExtraData(name, {
        name,
        value: '',
        mask: '',
        validator: '',
        uuid: uuid.value,
        question: stripHtml(props.content.text.value),
        sendData: props.content.send_to_lead_service?.value !== false,
    });
});

const updateValue = event => {
    const answer = event.target;
    const answerValue = answer.value;
    const answerLabel = answer.dataset.label;

    storeExtraData(name, 'value', answerValue);
    storeExtraData(name, 'label', answerLabel);
};

const check = event => {
    const questionWrapper = event.target.closest('.question');
    const buttons = questionWrapper.querySelectorAll('.button');
    const delay = questionWrapper.dataset.delay ? parseInt(questionWrapper.dataset.delay) : 0;
    const answer = questionWrapper.querySelector('input:checked');

    if (answer === null) {
        sbPopup(store.state.page.notificationText, 'error');
        return;
    }

    if (answer.dataset.showstopper !== '' && answer.dataset.showstopper !== 'none') {
        if (answer.dataset.hideShowstopperCloseButton === 'true') store.commit('dialog/preventClose');
        store.commit('dialog/show', answer.dataset.showstopper);
        return;
    }

    const answerValue = answer.value;
    const answerLabel = answer.dataset.label;
    const linkedFormInputIdentifier = event.target.dataset.linkedFormInput;
    const worktypeId = answer.dataset.worktypeId;
    const enquiryDashboardRedirectVersion = answer.dataset.enquiryDashboardRedirectVersion;
    const nextQuestion = getNextQuestion(answer.dataset.questionTo || '', store.state.quiz.currentQuestion);

    checkCompleted(name, buttons, props.preHook, nextQuestion, props.afterHook, answerValue, linkedFormInputIdentifier, worktypeId, enquiryDashboardRedirectVersion, delay);
    storeExtraData(name, 'value', answerValue);
    storeExtraData(name, 'label', answerLabel);
};
</script>

<style lang="scss">
@use 'zoe-quiz-radio-button';

.question-row-checkbox {
    --column-count-radio: v-bind('props.content.options.column_count.value');
}
</style>
